<script>
    import ImageObject from '@/components/page-file-edit/images/ImageObject.vue';
    import PaginationController from '@/components/base-components/PaginationController.vue';
    import { excludeFile } from '@/api/file.js';
    import { eraseBatches } from '@/scripts/eraseMarked.mjs';


    export default {
        data() {
            return {
                filesOutput: [],

                pageCount: 0,
                currentPage: 0,
                erasing: false,
                eraseNote: '',
            }
        },
        props: {
            // Overrides the "Files per page" setting for one page without
            // touching the store, which SAVE_LOCAL_STORAGE would persist.
            perPage: { type: Number, default: null },
            // Header text for a page where released/total means nothing (the
            // disputes list). Gets the count; null keeps file-edit's header.
            headerLabel: { type: Function, default: null },
            // The store holds one server page, not the whole list: the pager
            // moves CURRENT_PAGE and refetches instead of slicing IMAGES.
            serverPaged: { type: Boolean, default: false },
        },
        components: {
            ImageObject: ImageObject,
            PaginationController: PaginationController,
        },
        async mounted() {
            await this.calculatePageCount();
            await this.updatePage();
        },
        computed: {
            images() { return this.$store.getters.IMAGES },
            imageCount() { return this.$store.getters.IMAGE_COUNT },
            imageReleaseCount() { return this.$store.getters.IMAGE_RELEASED },
            fileInPage() { return this.perPage || this.$store.getters.FILE_IN_PAGE },
            assetType() { return this.$store.getters.ASSET_TYPE_IMAGES },
            // Over IMAGES, not this page's slice: on /file-edit the whole list is
            // loaded and paged here, so marks survive paging. A server-paged list
            // refetches per page and its marks go with it.
            markedCount() { return this.images.filter((f) => f.toDelete === true).length },
        },
        methods: {
            async calculatePageCount() {
                this.pageCount = await Math.ceil(this.imageCount / this.fileInPage - 1);
                this.currentPage = this.serverPaged ? this.$store.getters.CURRENT_PAGE : 0;
            },
            async updatePage() {
                // Rows leave the list in place (settled disputes, deletes); the
                // array keeps its identity, so the watcher below does not fire.
                this.pageCount = Math.ceil(this.imageCount / this.fileInPage - 1);
                if (this.currentPage > this.pageCount) this.currentPage = Math.max(this.pageCount, 0);
                if (this.serverPaged) {
                    // Every settled row shifts the rest up one; once the page is
                    // empty the same offset now holds the next rows, so refetch.
                    if (this.images.length === 0 && this.imageCount > 0) {
                        // pageCount above is fresh from total_count, so the
                        // clamped page is never past the end: one refetch, no loop.
                        await this.goToPage(Math.min(this.currentPage, this.pageCount));
                        return;
                    }
                    this.filesOutput = this.images;
                    return;
                }
                const pageStart = this.currentPage * this.fileInPage;
                const pageEnd = pageStart + this.fileInPage;
                this.filesOutput = await this.images.slice(pageStart, pageEnd);
            },
            async goToPage(page) {
                this.currentPage = page;
                if (this.serverPaged) {
                    this.$store.commit('CURRENT_PAGE', page);
                    // The page lives in the URL so a reload or a link to a
                    // colleague lands on the same page; the store reads it back.
                    const { page: _, ...query } = this.$route.query;
                    await this.$router.replace({ query: page > 0 ? { ...query, page } : query });
                    this.$store.commit('IS_LOAD', true);
                    await this.$store.dispatch('UPDATE_FILES');
                    this.$store.commit('IS_LOAD', false);
                } else {
                    this.updatePage();
                }
                window.location.href='#imageHeader';
            },

            // 869fhgjpg: erase every marked gup row for good, no confirm by design.
            // One request per provider (the endpoint takes one), stopping at the
            // first failure so nothing is reported as gone that wasn't.
            async eraseMarked() {
                if (this.erasing) return;
                const { batches, left } = eraseBatches(this.images);
                this.erasing = true;
                this.eraseNote = '';
                let erased = 0;
                let unremembered = 0;
                try {
                    for (const { providerId, ids } of batches) {
                        const out = await excludeFile(this.assetType, providerId, ids);
                        if (out == null) throw new Error('exclude failed');
                        erased += out.images ?? 0;
                        unremembered += (out.unremembered_ids ?? []).length;
                        for (const id of out.purged_ids ?? []) this.$store.commit('IMAGE_DELETE_BY_ID', id);
                    }
                } catch {
                    this.eraseNote = 'Delete stopped on an error. ';
                } finally {
                    this.erasing = false;
                }
                this.eraseNote += `${erased} deleted for good.`;
                if (unremembered > 0) this.eraseNote += ` ${unremembered} had no mirror path: GUP may upload them again.`;
                if (left > 0) this.eraseNote += ` ${left} skipped (not from GUP).`;
                await this.updatePage();
            },

            async nextPage() { await this.goToPage(this.currentPage + 1); },
            async backPage() { await this.goToPage(this.currentPage - 1); },
            async firstPage() { await this.goToPage(0); },
            async lastPage() { await this.goToPage(this.pageCount); }
        },
        watch: {
            images: {
                handler: async function() { 
                    await this.calculatePageCount();
                    await this.updatePage();
                }
            },
        }
    }
</script>


<template>
    <div
        class="file_container" 
        v-if="imageCount > 0"
    >
        <div v-if="markedCount > 0 || eraseNote" class="erase_bar">
            <span v-if="eraseNote" class="erase_note">{{ eraseNote }}</span>
            <button
                v-if="markedCount > 0"
                type="button"
                class="erase_button"
                :disabled="erasing"
                title="Off the shelves, out of S3 and the database; GUP won't upload them again"
                @click="eraseMarked"
            >{{ erasing ? 'Deleting…' : `Delete (${markedCount})` }}</button>
        </div>
        <div class="file_header" id="imageHeader">
            <label>
                <template v-if="headerLabel">{{ headerLabel(imageCount) }}</template>
                <template v-else>Images [{{ imageReleaseCount }} / {{ imageCount }}]</template>
            </label>
        </div>
        <ImageObject 
            v-for="fileValue in filesOutput" :key="fileValue.id"
            :file = "fileValue"
            :assetTypeID = "assetType"
            :functionUpdatePage = "updatePage"
        />
        <PaginationController 
            :currentPage = "currentPage"
            :maxPage = "pageCount"
            @back = "backPage"
            @next = "nextPage"
            @first = "firstPage"
            @last = "lastPage"
        />
    </div>
</template>


<style scoped>
    hr {
        width: 98%;
    }

    .file_container {
        padding: 10px;
    }
    .file_header {
        width: 100%;

        background-color: var(--header);
        color: var(--header-text);

        border-radius: 5px;

        padding-top: 5px;
        padding-bottom: 5px;
        padding-left: 5px;
        margin-bottom: 5px;

        font-size: 18px;
    }
    /* 869fhgjpg: pinned to the top while scrolling the list. */
    .erase_bar {
        position: sticky;
        top: 0;
        z-index: 5;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 12px;
        padding: 8px 10px;
        background-color: var(--background);
        border-bottom: 1px solid var(--header-separator);
    }
    .erase_button {
        min-height: 36px;
        padding: 4px 18px;
        border: none;
        border-radius: 5px;
        background-color: #fc424a;
        color: #ffffff;
        font-size: 15px;
        cursor: pointer;
    }
    .erase_button:disabled { opacity: 0.6; cursor: default; }
    .erase_note { font-size: 14px; }
</style>